import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatError, API } from "../lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import {
  ArrowLeft, ArrowUpRight, Quote, Star, Instagram, Loader2, ExternalLink,
  Share2, Facebook, Twitter, Copy, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PublicStory — shareable page for a single approved Founder Story.
 * Route: /stories/:id (PUBLIC — no auth wall so links work on social).
 * Backend: GET /api/stories/:id (only returns published + approved).
 */
export default function PublicStory() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/stories/${id}`)
      .then((r) => { if (alive) setStory(r.data); })
      .catch((e) => { if (alive) setError(formatError(e) || "Story not found"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  usePageMeta({
    title: story ? `${story.brand} — Founder Story · CPG Institute` : "Founder Story · CPG Institute",
    description: story ? story.story.slice(0, 180) : "A founder-authored launch story from the CPG Institute community.",
    url: typeof window !== "undefined" ? window.location.href : undefined,
    image: story ? `${API}/stories/${story.id}/instagram-card` : undefined,
  });

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-foreground">
      {/* Slim top bar */}
      <header className="border-b border-shell-border/70 bg-white/80 backdrop-blur sticky top-0 z-30" data-testid="public-story-header">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link
            to="/"
            data-testid="public-story-home-link"
            className="font-display text-lg tracking-tight text-foreground hover:text-primary transition"
          >
            CPG Institute
          </Link>
          <Link
            to="/#stories"
            data-testid="public-story-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All Stories
          </Link>
        </div>
      </header>

      {loading && (
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 flex items-center gap-3 text-sm text-muted-foreground" data-testid="public-story-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading story…
        </div>
      )}

      {!loading && error && (
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24" data-testid="public-story-error">
          <p className="text-xs uppercase tracking-[0.28em] text-primary font-bold">404</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">Story not found</h1>
          <p className="mt-4 text-muted-foreground max-w-lg">
            This founder story isn't published or has been taken down. Head back to the wall to see the ones that are live.
          </p>
          <Link
            to="/#stories"
            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-5 py-2.5 text-sm font-semibold hover:bg-shell-side/90 transition"
            data-testid="public-story-error-back"
          >
            Back to Founder Stories <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {!loading && story && (
        <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="public-story-main">
          {/* Ambient blobs */}
          <div className="pointer-events-none absolute inset-x-0 top-24 h-96 -z-10 overflow-hidden">
            <div className="absolute top-0 -left-16 w-72 h-72 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute -top-6 -right-10 w-80 h-80 rounded-full bg-sky-200/30 blur-3xl" />
          </div>

          <p className="text-xs uppercase tracking-[0.28em] text-primary font-bold" data-testid="public-story-eyebrow">
            Founder Story
          </p>
          <h1
            className="font-display text-4xl md:text-6xl mt-3 tracking-tight leading-[1.05]"
            data-testid="public-story-brand"
          >
            {story.brand}
          </h1>
          <p className="mt-3 text-muted-foreground text-sm">
            Published by{" "}
            <span className="font-semibold text-foreground" data-testid="public-story-author">
              {story.author_name || "Institute member"}
            </span>
            {story.featured && (
              <span
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                data-testid="public-story-featured-badge"
              >
                <Star className="w-2.5 h-2.5" /> Featured
              </span>
            )}
          </p>

          {/* Story body card */}
          <article
            className="mt-10 rounded-3xl border border-shell-border bg-white p-8 md:p-12 shadow-sm relative overflow-hidden"
            data-testid="public-story-card"
          >
            <div className="absolute inset-x-0 top-0 h-1 grad-band" />
            <Quote className="w-10 h-10 text-primary/25 -mb-2" />
            <p
              className="text-[19px] md:text-[22px] leading-[1.6] text-foreground whitespace-pre-line"
              data-testid="public-story-body"
            >
              {story.story}
            </p>

            <div className="mt-10 pt-6 border-t border-shell-border flex items-center gap-4 flex-wrap">
              <div className="w-12 h-12 rounded-full bg-shell-side text-white grid place-items-center text-sm font-bold" aria-hidden>
                {(story.author_name || story.brand || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{story.author_name || "Institute member"}</p>
                <p className="text-xs text-muted-foreground">{story.brand}</p>
              </div>
              {story.website && (
                <a
                  href={story.website.startsWith("http") ? story.website : `https://${story.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="public-story-website"
                  className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
                >
                  Visit brand <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </article>

          {/* Share row */}
          <section className="mt-10 rounded-2xl bg-white/60 border border-shell-border p-6" data-testid="public-story-share">
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="w-4 h-4 text-primary" />
              <p className="text-xs uppercase tracking-[0.22em] font-bold">Share this story</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={copyLink}
                data-testid="public-story-copy-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <a
                href={`${API}/stories/${story.id}/instagram-card`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-story-instagram-card"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
              >
                <Instagram className="w-3.5 h-3.5" /> Download Instagram card
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`${story.brand} on CPG Institute:`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-story-twitter"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
              >
                <Twitter className="w-3.5 h-3.5" /> Share on X
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-story-facebook"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
              >
                <Facebook className="w-3.5 h-3.5" /> Share on Facebook
              </a>
            </div>
          </section>

          {/* Bottom CTA back to landing */}
          <section className="mt-14 rounded-3xl bg-shell-side text-white p-8 md:p-12 relative overflow-hidden" data-testid="public-story-cta">
            <div className="absolute inset-x-0 top-0 h-1 grad-band" />
            <p className="text-xs uppercase tracking-[0.28em] text-shell-accent font-bold">Your story next</p>
            <h2 className="font-display text-2xl md:text-4xl mt-3 tracking-tight max-w-2xl">
              Every founder here published a story like this after finishing the Institute.
            </h2>
            <p className="mt-4 text-white/80 max-w-2xl text-sm md:text-base leading-relaxed">
              The same 16-module playbook, database access, and Launch Blueprint that shipped this brand is available to you.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link
                to="/account/subscription"
                data-testid="public-story-cta-pricing"
                className="inline-flex items-center gap-1.5 rounded-full bg-shell-accent text-shell-side px-6 py-3 text-sm font-bold hover:bg-shell-accent/90 transition"
              >
                See pricing <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/#stories"
                data-testid="public-story-cta-more"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:border-white/60 transition"
              >
                Read more stories
              </Link>
            </div>
          </section>
        </main>
      )}

      <footer className="border-t border-shell-border/70 mt-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-6 text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-3">
          <span>© {new Date().getFullYear()} CPG Institute</span>
          <Link to="/" className="hover:text-foreground">cpg.institute</Link>
        </div>
      </footer>
    </div>
  );
}
